const SPACING_KEY_PATTERN =
    /(Body|Description|Intro|Answer|Question|Header|Tagline)$/;
const LIST_OR_HEADING_PATTERN = /^([-*+]\s|\d+\.\s|#{1,6}\s|>)/;
const CODE_FENCE_PATTERN = /^(?:```|~~~)/;
const HTML_BLOCK_PATTERN = /^<\/?[a-zA-Z][\w:-]*.*>/;
const MANUAL_BREAK_PATTERN = /( {2}|\\|<br\s*\/?>(?:\s*)?)$/i;
const HORIZONTAL_RULE_PATTERN = /^([-*_])\1{2,}$/;
const SENTENCE_TERMINATOR_PATTERN = /[.!?\u2026]/;
const CLOSING_PUNCTUATION_PATTERN = /["')\]\u2019\u201D]/;

const DEFAULT_OPTIONS = {
    apply: 'auto',
    fieldKey: undefined,
};

function shouldApplySpacing(fieldKey, apply = 'auto') {
    if (apply === 'always') {
        return true;
    }

    if (apply === 'never') {
        return false;
    }

    if (!fieldKey) {
        return false;
    }

    const key = fieldKey.split('.').pop() || '';
    return SPACING_KEY_PATTERN.test(key);
}

function lineStartsNewBlock(line) {
    return (
        LIST_OR_HEADING_PATTERN.test(line) ||
        CODE_FENCE_PATTERN.test(line) ||
        HORIZONTAL_RULE_PATTERN.test(line) ||
        HTML_BLOCK_PATTERN.test(line)
    );
}

function shouldInsertParagraphGap(currentLine, nextLine) {
    if (typeof currentLine !== 'string' || typeof nextLine !== 'string') {
        return false;
    }

    const trimmedNext = nextLine.trim();
    if (!trimmedNext) {
        return false;
    }

    if (lineStartsNewBlock(trimmedNext)) {
        return false;
    }

    if (/^\s/.test(nextLine)) {
        // Preserve manual indent-based breaks (lists, code, etc.).
        return false;
    }

    if (MANUAL_BREAK_PATTERN.test(currentLine)) {
        // Respect explicit hard-break requests (two spaces, backslash, <br/>).
        return false;
    }

    const trimmedCurrent = currentLine.trimEnd();
    if (!trimmedCurrent) {
        return false;
    }

    if (lineStartsNewBlock(trimmedCurrent)) {
        return false;
    }

    let idx = trimmedCurrent.length - 1;
    while (idx >= 0 && CLOSING_PUNCTUATION_PATTERN.test(trimmedCurrent[idx])) {
        idx -= 1;
    }

    if (idx < 0) {
        return false;
    }

    return SENTENCE_TERMINATOR_PATTERN.test(trimmedCurrent[idx]);
}

export function normalizeNewlines(markdown) {
    if (typeof markdown !== 'string') {
        return markdown;
    }

    return markdown.replace(/\r\n?/g, '\n');
}

export function enforceParagraphSpacing(markdown, options = DEFAULT_OPTIONS) {
    if (typeof markdown !== 'string') {
        return markdown;
    }

    const { fieldKey, apply } = { ...DEFAULT_OPTIONS, ...options };
    const normalized = normalizeNewlines(markdown);

    if (!shouldApplySpacing(fieldKey, apply)) {
        return normalized;
    }

    const lines = normalized.split('\n');

    for (let i = 0; i < lines.length - 1; i += 1) {
        if (lines[i + 1].trim() === '') {
            continue;
        }

        if (shouldInsertParagraphGap(lines[i], lines[i + 1])) {
            lines.splice(i + 1, 0, '');
            i += 1;
        }
    }

    return lines.join('\n');
}

export const __TESTING__ = {
    SPACING_KEY_PATTERN,
    LIST_OR_HEADING_PATTERN,
    CODE_FENCE_PATTERN,
    HTML_BLOCK_PATTERN,
    HORIZONTAL_RULE_PATTERN,
    SENTENCE_TERMINATOR_PATTERN,
    CLOSING_PUNCTUATION_PATTERN,
    MANUAL_BREAK_PATTERN,
    shouldInsertParagraphGap,
    lineStartsNewBlock,
    shouldApplySpacing,
};
